import { OverlayModule } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeSv from '@angular/common/locales/sv';
import {
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  GcButtonComponent,
  GcLinkComponent,
  GcSnackbarsContainerComponent,
  IpoAlertComponent,
  IpoAlertsContainerComponent,
  IpoAlertsService,
  IpoBorderlessButtonComponent,
  IpoButtonComponent,
  IpoDropdownComponent,
  IpoLanguageService,
  IpoLoadingSpinnerComponent,
  IpoMatchMediaService,
  IpoModalComponent,
  IpoPageHeaderComponent,
  IpoPortalLayoutModule,
  IpoSideMenuButtonComponent,
  IpoSideMenuComponent,
  IpoStringService,
  IpoSvgIconComponent,
  IpoTextComponent,
  IpoTouchCheckService,
  IpoTranslatePipe,
  stringServiceFactory,
} from '@ip-only/front-end-library';
import { ApiBaseUrlInterceptor } from './api-base-url.interceptor';
import { MicroserviceRegistryInterceptor } from './microservice-registry.interceptor';
import { AppAuthGuard } from './app-auth-guard';
import { initializer } from './app-init';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ConstructionDocumentsAppModule } from './construction-documents-app/construction-documents-app.module';
import { DeliveryOverviewAppModule } from './delivery-overview-app/delivery-overview-app.module';
import { GuideMenuComponent } from './guide/guide-menu/guide-menu.component';
import { GuideModalComponent } from './guide/guide-modal.component';
import { GuideTextComponent } from './guide/guide-text/guide-text.component';
import { InstallationReportingAppModule } from './installation-reporting-app/installation-reporting-app.module';
import { KeycloakModule } from './keycloak/keycloak.module';
import { OfflineKeycloakService } from './keycloak/service/keycloak.service';
import { AppLayoutComponent } from './layout/app-layout.component';
import { SideMenuComponent } from './layout/menus/side-menu/side-menu.component';
import { TopMenuComponent } from './layout/menus/top-menu/top-menu.component';
import { ProjectFolderOpenerComponent } from './project-folder-opener/project-folder-opener.component';
import { ResourceCompleteComponent } from './resource-complete/resource-complete.component';
import { ResourceFinderComponent } from './resource-finder/resource-finder.component';
import { FeedbackAlertComponent } from './shared/feedback-alert/feedback-alert.component';
import { AnalyticsService } from './shared/posthug-analytics/analytics.service';
import { SharedModule } from './shared/shared.module';
import { SpanProgressModule } from './span-progress-app/span-progress.module';
import { default as en, default as fi } from './strings.en';
import sv from './strings.sv';
import irEn from './installation-reporting-app/strings.en';
import irSv from './installation-reporting-app/strings.sv';
import irFi from './installation-reporting-app/strings.fi';
import { UpdateFooterComponent } from './updates/update-footer.component';
import { ContractorUserApiService } from './user/api/contractor-user-api.service';
import { ResourcesService } from './user/resources.service';
import { UserService } from './user/user.service';

registerLocaleData(localeSv);

@NgModule({
  declarations: [
    AppComponent,
    AppLayoutComponent,
    TopMenuComponent,
    ResourceFinderComponent,
    UpdateFooterComponent,
    GuideModalComponent,
    GuideMenuComponent,
    GuideTextComponent,
    ProjectFolderOpenerComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    IpoTextComponent,
    IpoTranslatePipe,
    IpoAlertComponent,
    IpoAlertsContainerComponent,
    IpoButtonComponent,
    IpoLoadingSpinnerComponent,
    IpoPortalLayoutModule,
    IpoSideMenuComponent,
    IpoSideMenuButtonComponent,
    IpoPageHeaderComponent,
    IpoSvgIconComponent,
    IpoDropdownComponent,
    IpoModalComponent,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    KeycloakModule,
    SharedModule,
    SpanProgressModule,
    IpoBorderlessButtonComponent,
    OverlayModule,
    InstallationReportingAppModule,
    DeliveryOverviewAppModule,
    ConstructionDocumentsAppModule,
    GcSnackbarsContainerComponent,
    FeedbackAlertComponent,
    ResourceCompleteComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
    }),
    GcLinkComponent,
    GcButtonComponent,
    SideMenuComponent,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initializer(
        inject(OfflineKeycloakService),
        inject(UserService),
        inject(ResourcesService),
        inject(ContractorUserApiService),
        inject(IpoTouchCheckService),
        inject(AnalyticsService),
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseUrlInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MicroserviceRegistryInterceptor,
      multi: true,
    },
    AppAuthGuard,
    IpoMatchMediaService,
    IpoAlertsService,
    IpoLanguageService,
    IpoTouchCheckService,
    {
      provide: IpoStringService,
      useFactory: stringServiceFactory({
        en: { ...en, ...irEn },
        sv: { ...sv, ...irSv },
        fi: { ...fi, ...irFi },
      }),
      deps: [IpoLanguageService],
    },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'sv' },
  ],
})
export class AppModule {}
